import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { EXPEDITE_ORDER } from '../../lib/graphql/mutations';
import { GET_RENTAL_PRICING } from '../../lib/graphql/mutations'; // Changed from queries to mutations
import { formatCentsAsUSD } from '../../lib/currency';
import { WHITE_LABEL_STRIPE_PROMISES } from '../../lib/white-label';
import { analytics } from '../../lib/analytics';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button } from '../ui/button';
import { HeadingTwo } from '../Text';

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
`;

const ExpediteModal = ({
  orderId,
  botId,
  botName,
  currentStartDate,
  listingId, // Add this prop
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [expeditePrice, setExpeditePrice] = useState(0);

  const [getRentalPricing, { data: pricingData, loading: pricingLoading }] =
    useMutation(GET_RENTAL_PRICING);

  const [expediteOrder] = useMutation(EXPEDITE_ORDER);

  useEffect(() => {
    getRentalPricing({
      variables: {
        priceData: {
          start: dayjs().add(5, 'minutes'),
          end: dayjs(currentStartDate),
          listingId: listingId,
          botId: botId,
        },
      },
    }).catch((err) => console.error(err));
  }, [currentStartDate, listingId]);

  useEffect(() => {
    if (pricingData?.getRentalPricing) {
      setExpeditePrice(pricingData.getRentalPricing.totalPrice);
    } else {
      setExpeditePrice(-1);
    }
  }, [pricingData]);

  const handleExpedite = async () => {
    setIsLoading(true);
    try {
      const { data } = await expediteOrder({
        variables: { orderId },
      });

      if (data?.expediteOrder) {
        analytics.track('Expedite Order Initiated', {
          orderId,
          botId,
          botName,
          expeditePrice,
        });

        // The expediteOrder mutation now returns a string (sessionId)
        const sessionId = data.expediteOrder;

        // We need to determine the whiteLabelOrg some other way, perhaps from a context or prop
        const whiteLabelOrg = 'default'; // Replace this with the correct way to get the whiteLabelOrg

        const stripe = await WHITE_LABEL_STRIPE_PROMISES[whiteLabelOrg];
        await stripe.redirectToCheckout({ sessionId });
      }
    } catch (error) {
      console.error('Error expediting order:', error);
      alert('Failed to expedite order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (pricingLoading) {
    return (
      <ModalWrapper>
        <ModalContent>
          <HeadingTwo>Loading...</HeadingTwo>
        </ModalContent>
      </ModalWrapper>
    );
  }

  if (!pricingData?.getRentalPricing) {
    return (
      <ModalWrapper>
        <ModalContent>
          <HeadingTwo>Couldn't Expedite Your Order</HeadingTwo>
          <p>No availability found for expedited start date.</p>
          <Button onClick={onClose} variant="secondary" className="float-right">
            Close
          </Button>
        </ModalContent>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper>
      <ModalContent>
        <HeadingTwo>Are you sure?</HeadingTwo>
        <div className="flex flex-col space-y-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center">
            <div className="w-24 text-sm text-gray-500">From</div>
            <div className="flex items-center space-x-2">
              <div className="h-8 px-3 flex items-center bg-white border rounded-md">
                {dayjs(currentStartDate).format('MMMM D, YYYY h:mm A')}
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-24 text-sm text-gray-500">To</div>
            <div className="flex items-center space-x-2">
              <div className="h-8 px-3 flex items-center bg-green-50 border-green-200 border rounded-md text-green-700">
                {dayjs().format('MMMM D, YYYY h:mm A')} (Now)
              </div>
            </div>
          </div>

          <div className="flex items-center border-t border-gray-200 pt-4 mt-2">
            <div className="w-24 text-sm text-gray-500">Cost</div>
            <div className="text-lg font-medium">
              {formatCentsAsUSD(expeditePrice)}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 float-right">
          <Button onClick={handleExpedite} disabled={isLoading}>
            {isLoading ? 'Processing...' : 'Confirm Expedite'}
          </Button>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
        </div>
      </ModalContent>
    </ModalWrapper>
  );
};

export default ExpediteModal;
